import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const badgeVariants = cva(
  "inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden",
  {
    variants: {
      variant: {
        default: "border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90",
        secondary: "border-transparent bg-secondary font-semibold text-secondary-foreground [a&]:hover:bg-secondary/90",
        destructive:
          "border-transparent bg-red-100 text-red-600 [a&]:hover:bg-red-200 focus-visible:ring-red-500/20 dark:bg-red-600/20 dark:text-red-600 dark:[a&]:hover:bg-red-500/30",
        positive:
          "border-transparent bg-green-100 text-green-600 [a&]:hover:bg-green-200 focus-visible:ring-green-500/20 dark:bg-green-600/20 dark:text-green-600 dark:[a&]:hover:bg-green-500/30",
        info: "border-transparent bg-sky-100 text-sky-600 [a&]:hover:bg-sky-200 focus-visible:ring-sky-500/20 dark:bg-sky-600/20 dark:text-sky-600 dark:[a&]:hover:bg-sky-500/30",
        warning:
          "border-transparent bg-amber-100 text-amber-600 [a&]:hover:bg-amber-200 focus-visible:ring-amber-500/20 dark:bg-amber-600/20 dark:text-amber-600 dark:[a&]:hover:bg-amber-500/30",
        outline: "text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

function Badge({
  className,
  variant,
  asChild = false,
  ...props
}: React.ComponentProps<"span"> & VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : "span";

  return <Comp data-slot="badge" className={cn(badgeVariants({ variant }), className)} {...props} />;
}

// https://github.com/shadcn-ui/ui/issues/1534
// eslint-disable-next-line react-refresh/only-export-components
export { Badge, badgeVariants };
